<template>
  <SectionContainer
      :colors="colors"
      :background-variant="backgroundVariant"
      class="w-full"
      v-if="shownItems.length > 0 || title"
  >
    <div class="w-full flex flex-col items-center px-4 sm:px-6">
      <component :is="headingTag" v-if="title" class="text-3xl font-bold text-center" style="color: var(--theme-text-heading);">
        {{ title }}
      </component>
      <div class="w-full max-w-3xl mt-8 flex flex-col gap-4">
        <component
            :is="cardComponent"
            v-for="(item, index) in shownItems"
            :key="index"
            :colors="colors"
            :variant="backgroundVariant"
            class="w-full"
        >
          <details class="faq-item group" :open="index === 0">
            <summary class="faq-summary flex items-center gap-4 cursor-pointer px-5 py-4 text-left">
              <span class="flex-1 text-lg font-semibold" style="color: var(--theme-text-heading);">{{ item.question }}</span>
              <NavArrowDown class="faq-chevron w-5 h-5 shrink-0 transition-transform duration-200" style="color: var(--brand-accent);" aria-hidden="true"/>
            </summary>
            <div class="faq-answer px-5 pb-5 text-base leading-relaxed space-y-3"
                 style="color: var(--theme-text-body);"
                 v-html="answerHtml(item.answer)"></div>
          </details>
        </component>
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {computed} from "vue";
import {NavArrowDown} from "@iconoir/vue";
import SectionContainer from "@/components/SectionContainer.vue";
import {useThemedComponent} from "@/composables/useThemedComponent.js";
import {safeMarkdown} from "@/utils/safeMarkdown.js";
const headingTag = useSectionHeadingTag();

// "Questions and answers" (Website Studio type `faq`): {title, items: [{question, answer}]}.
// Native <details>/<summary>, so it opens and closes by keyboard and screen
// reader with no script, and works before hydration.
const props = defineProps({
  title: {type: String, default: ""},
  items: {type: Array, default: () => []},
  colors: {type: Object, default: () => ({})},
  backgroundVariant: {type: Number, default: 1},
});

const cardComponent = useThemedComponent("card");

const shownItems = computed(() =>
    (props.items || []).filter((i) => i && typeof i.question === "string" && i.question.trim() !== "")
);

const answerHtml = (answer) => safeMarkdown(answer);
</script>

<style scoped>
/* The base layer colours every <p> gray-800; follow the theme's body text
   instead, so dark themes (noir, neon) stay readable. */
.faq-answer :deep(p),
.faq-answer :deep(li) {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}
.faq-summary {
  list-style: none;
  min-height: 44px;
}
.faq-summary::-webkit-details-marker {
  display: none;
}
.faq-summary:focus-visible {
  outline: 3px solid var(--brand-accent, #2e9e8f);
  outline-offset: 2px;
}
details[open] .faq-chevron {
  transform: rotate(180deg);
}
.faq-answer :deep(a) {
  color: var(--brand-accent);
  text-decoration: underline;
}
.faq-answer :deep(ul) {
  list-style: disc;
  padding-left: 1.25rem;
}
.faq-answer :deep(ol) {
  list-style: decimal;
  padding-left: 1.25rem;
}
</style>
